import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ProductSummaryItem } from '@backbase/data-ang/arrangements';
import { CatalogItem } from 'banesco-catalogues-api';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import { BeneficiaryList } from './enums/beneficiary-list';
import { SearchBoxComponent } from '@backbase/ui-ang';

export enum ListState {
  EMPTY = 'empty',
  LIST = 'list',
  FULL_LIST = 'full-list',
  LOADING = 'loading',
  FETCHING = 'fetching',
  FETCHING_FAILURE = 'fetching-failure',
  NO_RESULT = 'no-result',
  ERROR = 'error',
}

export interface BeneficiaryListState {
  accounts: ListState;
  contacts: ListState;
  catalogues: ListState;
}

export const defaultBeneficiaryTabsConfig = [
  BeneficiaryList.CONTACTS,
  BeneficiaryList.ACCOUNTS,
  BeneficiaryList.CATALOGUES,
];

@Component({
  selector: 'bn-beneficiary-selector-ui',
  templateUrl: './beneficiary-selector-ui.component.html',
  styleUrls: ['./beneficiary-selector-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class BeneficiarySelectorUiComponent implements OnDestroy, OnInit {
  @ViewChild('searchElem') searchElemExt!: SearchBoxComponent<any>;
  @ViewChild('searchElemIn') searchElemIn!: SearchBoxComponent<any>;
  private _accounts!: ProductSummaryItem[];
  private _contacts!: any[];
  private _catalogues!: CatalogItem[];
  readonly listingState = ListState;
  searchLabel = $localize`:@@beneficiary-selector.search-label:Search`;

  state: BeneficiaryListState = {
    contacts: ListState.EMPTY,
    accounts: ListState.EMPTY,
    catalogues: ListState.EMPTY,
  };

  /**
   * Total count of accounts
   */
  @Input() totalAccounts = 0;

  /**
   * Total count of contacts
   */
  @Input() totalContacts = 0;

  /**
   * Total count of catalogues
   */
  @Input() totalCatalogues = 0;

  /**
   * Total count of contacts items before sub-accounts extract
   */
  @Input() totalContactsItemsCount = 0;

  /**
   * List of contacts
   */
  @Input() set contacts(value: any[]) {
    if (this.state.contacts === ListState.FETCHING && value) {
      this._contacts = [...this._contacts, ...value];
    } else {
      this._contacts = value;
    }
    this.updateListStateOnDataChange(BeneficiaryList.CONTACTS, this._contacts);
  }

  get contacts(): any[] {
    return this._contacts;
  }

  /**
   * List of contacts
   */
  @Input() set catalogues(value: any[]) {
    if (this.state.catalogues === ListState.FETCHING && value) {
      this._catalogues = [...this.catalogues, ...value];
    } else {
      this._catalogues = value;
    }
    this.updateListStateOnDataChange(BeneficiaryList.CATALOGUES, this._catalogues);
  }

  get catalogues(): any[] {
    return this._catalogues;
  }

  /**
   * List of own accounts
   */
  @Input() set accounts(value: ProductSummaryItem[]) {
    if (this.state.accounts === ListState.FETCHING && value) {
      this._accounts = [...this._accounts, ...value];
    } else {
      this._accounts = value;
    }

    this.updateListStateOnDataChange(BeneficiaryList.ACCOUNTS, this._accounts);
  }

  get accounts(): ProductSummaryItem[] {
    return this._accounts;
  }

  /**
   * Tabs configuration
   */
  @Input() tabs!: Array<BeneficiaryList>;

  /**
   * Pipe to initiate dropdown closure from outside
   */
  @Input() closeDropdownRequest$: Subject<boolean> = new Subject();

  /**
   * Placeholder for the search box
   */
  @Input() placeholder!: string;

  /**
   * Title for the beneficiary selector
   */
  @Input() title!: string;
  /**
   * Helper text for the beneficiary selector. Appears inline next to the label.
   */
  @Input() helperText!: string;

  @Input() type: 'contact' | 'account' | 'cataloge' = 'contact';

  /**
   * Flag to denote if the beneficiary selector has errors.
   */
  @Input() set hasError(error: boolean) {
    if (error) {
      if (!this.search.touched) {
        this.search.markAsTouched();
      }
    }
  }

  /**
   * aria-label for the search box arrow icon
   */
  @Input('aria-label') ariaLabel!: string;

  /**
   * Dropdown icon that would be shown alongside with textbox
   */
  @Input() dropdownIcon = 'toggle-down';

  /**
   * Selected account event emitter
   */
  @Output() selectedAccount = new EventEmitter();

  /**
   * Selected contact event emitter
   */
  @Output() selectedContact = new EventEmitter();

  /**
   * Selected catalog event emitter
   */
  @Output() selectedCatalog = new EventEmitter();

  /**
   * Search string
   */
  @Output() searchValue = new EventEmitter<string>();

  /**
   * Fetch more items event handler
   */
  @Output() fetchMoreItems = new EventEmitter<{
    page: number;
    type: BeneficiaryList;
  }>();

  /**
   * Enter account detail event
   */
  @Output() manualEdit = new EventEmitter<string>();

  /**
   * Event emitted search is blurred
   */
  @Output() searchBlur = new EventEmitter<string>();

  /**
   * Clear search value
   */
  @Output() clear = new EventEmitter<string>();

  /**
   * Time delay between each search requests in milliseconds. Defaults to 300
   */
  @Input() debounceTime = 300;

  /**
   * Selected item, contact or account
   */
  @Input()
  set selectedItem(selectedItem: any | ProductSummaryItem | undefined | CatalogItem) {
    if (selectedItem?.name) {
      let value = selectedItem?.alias || selectedItem?.name || selectedItem?.desc;
      if (selectedItem?.accounts?.[0]?.alias || selectedItem?.accounts?.[0]?.name) {
        value += ` - ${selectedItem?.accounts?.[0]?.alias || selectedItem?.accounts?.[0]?.name}`;
      }

      this.search.patchValue(selectedItem.name);
      this._selectedItem = selectedItem;
    }
  }

  @Input()
  set selectedEditItem(selectedBeneficiary: any | ProductSummaryItem | undefined | CatalogItem) {
    if (selectedBeneficiary) {
      if(selectedBeneficiary?.productKindName){
        this.selectAccount(selectedBeneficiary);
      } else if (selectedBeneficiary?.accounts?.length) {
        this.selectContact(selectedBeneficiary);
      } else {
        this.selectCatalog(selectedBeneficiary);
      }
    }
  }

  get selectedItem() {
    return this._selectedItem;
  }

  @Input() disabled = false;
  @Input() readOnly = false;
  /**
   * Form control for search value.
   */
  @Input() search = new FormControl();

  /**
   * Flag to denote if the account has only closed payment permission.
   * Default value is `false`
   */
  @Input() isClosedPayment = false;

  /**
   * Flag to denote if the beneficiary field is disabled.
   * Default value is `false`
   */
  @Input() isBeneficiaryDisabled = false;
  /**
   * Flag to denote if the manual edit feature is disabled.
   * Default value is `false`
   */
  @Input() isManualEditDisabled = false;

  searchIsFocused = false;
  private _selectedItem?: any | ProductSummaryItem;
  readonly debounceSearch$ = new Subject<string>();
  readonly gc$ = new Subject();
  subsForm: Subscription = Subscription.EMPTY;

  private pageState = {
    [BeneficiaryList.CONTACTS]: {
      page: 0,
      shouldLoadMore: () => this.totalContacts > this.totalContactsItemsCount,
    },
    [BeneficiaryList.ACCOUNTS]: {
      page: 0,
      shouldLoadMore: () => this.totalAccounts > this._accounts.length,
    },
    [BeneficiaryList.CATALOGUES]: {
      page: 0,
      shouldLoadMore: () => this.totalCatalogues > this._catalogues.length,
    },
  };

  @ViewChild('dd') dropdownItem?: NgbDropdown;

  constructor(private changeDetector: ChangeDetectorRef,) {
    this.debounceSearch$
      .pipe(
        debounceTime(this.debounceTime),
        distinctUntilChanged(),
        takeUntil(this.gc$),
        tap(() => {
          this.listStateReducer({
            [BeneficiaryList.ACCOUNTS]: ListState.LOADING,
            [BeneficiaryList.CONTACTS]: ListState.LOADING,
            [BeneficiaryList.CATALOGUES]: ListState.LOADING,
          });
        }),
      )
      .subscribe((term: string) => {
        this.searchValue.emit(term);
        this.changeDetector.detectChanges();
      });
  }

  ngOnInit() {
    this.listStateReducer({
      [BeneficiaryList.ACCOUNTS]: ListState.LOADING,
      [BeneficiaryList.CONTACTS]: ListState.LOADING,
      [BeneficiaryList.CATALOGUES]: ListState.LOADING,
    });

    this.closeDropdownRequest$.pipe(takeUntil(this.gc$)).subscribe((closeFlag: boolean) => {
      if (closeFlag && this.dropdownItem) {
        this.dropdownItem.close();
      }
    });
  }

  ngOnDestroy() {
    this.gc$.next();
    this.gc$.complete();
    this.subsForm.unsubscribe();
  }

  private listStateReducer(update: { [key: string]: ListState }): void {
    this.state = { ...this.state, ...update };
  }

  selectAccount(account: ProductSummaryItem) {
    this.selectedAccount.emit(account);
    this.search.patchValue(account.name);
  }

  selectCatalog(catalog: CatalogItem) {
    this.selectedCatalog.emit(catalog);
    this.search.patchValue(`${catalog.desc} - ${catalog.id}`);
  }

  selectContact(contact: any) {
    let value = contact?.alias || contact?.name || contact?.desc;
    if (contact?.accounts?.[0]?.alias || contact?.accounts?.[0]?.name) {
      value += ` - ${contact?.accounts?.[0]?.alias || contact?.accounts?.[0]?.name}`;
    }

    this.selectedContact.emit(contact);
    this.search.patchValue(value);
  }

  handleSearch(searchString = '') {
    if (!searchString) {
      this.searchElemExt.value = '';
      this.searchElemIn.value = '';
    }
    if (this.searchIsFocused) {
      this.debounceSearch$.next(searchString?.trim());

      // reset page count in pageState when search is triggered
      this.pageState[BeneficiaryList.ACCOUNTS].page = 0;
      this.pageState[BeneficiaryList.CONTACTS].page = 0;
      this.pageState[BeneficiaryList.CATALOGUES].page = 0;
    }
  }

  onBlur() {
    this.searchIsFocused = false;
    this.searchBlur.emit(this.search.value ? this.search.value.trim() : '');
  }

  focusHandler() {
    this.searchIsFocused = true;
    if (this.dropdownItem) {
      this.dropdownItem.open();
    }
  }

  onClear() {
    this.handleSearch('');
    this.search.reset();
    this.clear.emit('');
  }

  handleDropdownClose(opened: boolean, dropdown: NgbDropdown) {
    if (!opened && this.searchIsFocused) {
      dropdown.open();
    }
  }

  onManualEdit() {
    if (!this.isManualEditDisabled) {
      this.manualEdit.emit(this.search.value ? this.search.value.trim() : '');
    }
  }

  fetchMoreItemsAction(listType: BeneficiaryList) {
    const { shouldLoadMore } = this.pageState[listType];
    let { page: _page } = this.pageState[listType];

    if (shouldLoadMore() && this.state[listType] === ListState.LIST) {
      this.listStateReducer({ [listType]: ListState.FETCHING });
      this.fetchMoreItems.emit({ page: ++_page, type: listType });

      // update page state after being incremented
      this.pageState[listType].page = _page;
    }
  }

  private updateListStateOnDataChange(listType: BeneficiaryList, value: any): void {
    let _state = this.state[listType];
    const { FETCHING, FETCHING_FAILURE, ERROR, NO_RESULT, EMPTY, LIST, FULL_LIST } = ListState;

    if (!value || value.error) {
      _state === FETCHING ? (_state = FETCHING_FAILURE) : (_state = ERROR);
    } else if (!value?.length) {
      this.search && this.search!.value ? (_state = NO_RESULT) : (_state = EMPTY);
    } else if (!this.pageState[listType].shouldLoadMore()) {
      _state = FULL_LIST;
    } else {
      _state = LIST;
    }

    this.listStateReducer({ [listType]: _state });
  }
}
